<template>
  <div
    :class="isMenuOptionsOpen ? 'restore-point-open-options': 'restore-point'"
  >
    <div>
      <div
        v-if=" restorePoint.description && restorePoint.description !== '' "
        class="flex restore-point__header"
      >
        <p
          :class="[
            'restore-point__name truncate-name big-box',
            isRecentlyApplied ? 'name-and-badge' : ''
          ]"
        >
          {{ restorePoint.name }}
        </p>
        <mi-badge v-if="isRecentlyApplied" class="recently-applied-badge">Recently applied</mi-badge>
      </div>
      <div class="flex restore-point__content">
        <div>
          <div
            v-if="!restorePoint.description || restorePoint.description === '' "
            class="flex restore-point__header restore-point__header-small"
          >
            <p
              :class="[
                'restore-point__name truncate-name small-box',
                isRecentlyApplied ? 'name-and-badge' : ''
              ]"
            >
              {{ restorePoint.name }}
            </p>
            <mi-badge v-if="isRecentlyApplied" class="recently-applied-badge">
              Recently applied
            </mi-badge>
          </div>
          <div class="description truncate-description">{{ restorePoint.description || "" }}</div>
          <p class="restore-point__options-selected">{{ restorePoint.numberOfSelectedOptions }} options selected</p>
        </div>
        <div class="restore-point__actions">
          <mi-btn
            class="drawer-restore-point"
            flat
          >
            <q-icon size="small">
              <img src="@/assets/images/dots_vertical.svg" alt="" />
            </q-icon>
            <mi-menu
              @mouseenter="toggleIsMenuOptionsOpen(true)"
              @focusin="toggleIsMenuOptionsOpen(true)"
              @hide="toggleIsMenuOptionsOpen(false)"
            >
              <mi-list bordered>
                <q-list class="actions-column__list">
                  <mi-list-item clickable @click="handleReviewDetailsModalOpen(true)">
                    <q-item-section>
                      <q-item-label>Review details</q-item-label>
                    </q-item-section>
                  </mi-list-item>
                  <mi-list-item
                    v-close-popup
                    :class="!isUserAuthorized ? 'disabled' : ''"
                    :clickable="isUserAuthorized"
                    @click="handleOpenModal()"
                  >
                    <q-item-section>
                      <q-item-label>Delete</q-item-label>
                    </q-item-section>
                  </mi-list-item>
                </q-list>
              </mi-list>
            </mi-menu>
          </mi-btn>
          <mi-btn class="apply-btn" @click="applyRestorePointBtnHandler">Apply</mi-btn>
        </div>
      </div>
    </div>
  </div>
  <delete-confirmation-modal
    v-model="confirmationModal"
    :restore-point="restorePoint"
    :configuration-id="configurationId"
    :handle-close-delete-modal="handleCloseDeleteModal"
    :handle-delete-modal="handleDeleteModal"
  ></delete-confirmation-modal>
  <review-details-modal
    :model-value="isReviewDetailsModalOpen"
    :toggle-model-value="handleReviewDetailsModalOpen"
    :restore-point-id="restorePoint.id"
    :configuration-id="configurationId"
    :is-user-authorized="isUserAuthorized"
    :refresh-restore-points="listRestorePoints"
  ></review-details-modal>
</template>

<script>
  import { useStore } from 'vuex'
  import { ref } from 'vue'
  import MiListItem from '@/packages/@mi-library/MiList/MiListItem.vue'
  import DeleteConfirmationModal
    from '@/components/pac/configuration/restore-point-drawer/tabs/DeleteConfirmationModal.vue'
  import {
    getRestorePointData,
    showHTTPErrorNotification,
    deleteRestorePoint
  } from '../../../../../api'
  import notify from '../../../../../utils/notify'
  import * as notificationTypes from '../../../../../plugins/quasar/notifications/notificationTypes'
  import MiBadge from '../../../../../packages/@mi-library/MiBadge/MiBadge.vue'
  import ReviewDetailsModal from './ReviewDetailsModal.vue'

  export default {
    name: 'RestorePointBox',
    components: { MiListItem, MiBadge, DeleteConfirmationModal, ReviewDetailsModal },
    props: {
      restorePoint: {
        type: Object,
        required: true
      },
      configurationId: {
        type: String,
        required: true
      },
      applyRestorePointInProgress: {
        type: Boolean,
        required: true
      },
      changeRecentlyApplied: {
        type: Function,
        required: true
      },
      isRecentlyApplied: {
        type: Boolean,
        required: true
      },
      listRestorePoints: {
        type: Function,
        required: true,
        default: () => {}
      },
      isUserAuthorized: {
        type: Boolean,
        required: true
      }
    },
    emits: ['update:applyRestorePointInProgress', 'closeSideBar'],
    setup(props, { emit }) {
      const store = useStore()
      let applyingNotificationFn = ref(null)
      const restorePointMenuItems = ref({
        'review-details': {
          title: 'Review Details'
        },
        'delete-restore-point': {
          title: 'Delete'
        }
      })
      const confirmationModal = ref(false)
      const isMenuOptionsOpen = ref(false)
      const isReviewDetailsModalOpen = ref(false)

      const updatePageState = ({ restorePointInProgress = false }) => {
        emit('update:applyRestorePointInProgress', restorePointInProgress)
        store.commit('pac/SET_IS_RESTORE_POINT_APPLYING', restorePointInProgress)

        if (!restorePointInProgress) {
          emit('closeSideBar')
        }
      }

      const toggleIsMenuOptionsOpen = value => {
        isMenuOptionsOpen.value = value
      }

      const updateCurrentConfiguration = async configurationRestored => {
        store.commit('pac/CLEAR_SELECTED_CHOICES')
        store.commit(
          'pac/SET_SELECTED_CHOICES',
          configurationRestored.selectedChoices.map(selectedChoice => selectedChoice.choice)
        )
        store.commit('pac/CLEAR_POSSIBLE_COMBINATION_CHOICES_IDS')
        store.commit(
          'pac/SET_CURRENT_CONFIGURATION',
          {
            ...store.state.pac.currentConfiguration,
            selectedChoices: configurationRestored.selectedChoices,
            validation: configurationRestored.validation,
            ignoreList: configurationRestored.ignoreList
          }
        )
        store.commit('pac/SET_VALIDATION', { results: [], initialState: true })
      }

      const applyRestorePointBtnHandler = async () => {
        if (props.applyRestorePointInProgress) {
          notify({
            content: 'There is already a restore point in progress, please wait.',
            type: notificationTypes.BASIC_INFO,
            position: 'bottom'
          })
        }
        else {
          try {
            updatePageState({ restorePointInProgress: true })

            applyingNotificationFn = notify({
              content: `${ props.restorePoint.name } being applied.`,
              type: notificationTypes.BASIC_INFO,
              position: 'bottom',
              spinner: true,
              timeout: 0
            })

            const restorePointData = await getRestorePointData(props.configurationId, props.restorePoint.id)
            await updateCurrentConfiguration(restorePointData.configuration)
            props.changeRecentlyApplied(props.restorePoint.id)
          }
          catch (e) {
            showHTTPErrorNotification({
              status: 'restoring could not be completed',
              message: e,
              custom: true
            })
          }
          finally {
            updatePageState({ restorePointInProgress: false })
            applyingNotificationFn()
          }
        }
      }

      const handleOpenModal = () => {
        confirmationModal.value = true
      }

      const handleCloseDeleteModal = () => {
        confirmationModal.value = false
      }

      const handleDeleteModal = async () => {
        await deleteRestorePoint(props.configurationId, props.restorePoint.id)
        handleCloseDeleteModal()
        await props.listRestorePoints()
        notify({
          content: 'Restore point deleted.',
          type: notificationTypes.BASIC_INFO,
          position: 'bottom'
        })
      }

      const handleReviewDetailsModalOpen = value => {
        isReviewDetailsModalOpen.value = value
      }

      return {
        applyingNotificationFn,
        applyRestorePointBtnHandler,
        restorePointMenuItems,
        handleOpenModal,
        confirmationModal,
        handleCloseDeleteModal,
        handleDeleteModal,
        toggleIsMenuOptionsOpen,
        isMenuOptionsOpen,
        handleReviewDetailsModalOpen,
        isReviewDetailsModalOpen
      }
    }
  }
</script>

<style lang="scss" scoped>
  /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix */
  .description {
    font-family: $mi-typography-font-family;
    font-weight: 400;
    font-size: 12px;
    line-height: 150%;
    letter-spacing: 0;
    color: $mi-anthracite-800;
    margin: 4px 0;
  }

  .restore-point {
    border: solid 1px $mi-anthracite-100;
    padding: 16px;
    margin: 4px 0;

    &__name {
      font-family: $mi-typography-font-family;
      font-weight: 700;
      font-size: 14px;
      line-height: 100%;
      margin-bottom: 4px;
      color: $mi-anthracite-800;
    }

    &__options-selected {
      font-family: $mi-typography-font-family;
      font-weight: 400;
      font-size: 12px;
      line-height: 150%;
      vertical-align: bottom;
      color: $mi-anthracite-500;
      margin-bottom: 0;
    }

    .apply-btn {
      display: none;
    }

    &__header {
      justify-content: space-between;
      width: 291px;
    }

    &__content {
      flex-wrap: nowrap;
      justify-content: space-between;
    }

    &__actions {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      gap: 8px;
    }

    .recently-applied-badge {
      font-size: 10px;
      width: 83px;
      height: 16px;
      padding: 0 4px;
      border-radius: 24px;
      background-color: $mi-anthracite-50;
      font-family: $mi-typography-font-family;
      font-weight: 400;
      line-height: 100%;
      letter-spacing: 0;
      text-align: center;
      vertical-align: middle;
      color: $mi-anthracite-800;
    }

    .drawer-restore-point {
      display: none;
    }
  }

  // stylelint-disable declaration-no-important
  .restore-point-open-options {
    .drawer-restore-point {
      background: $mi-anthracite-50 !important;
    }
  }

  .restore-point-open-options,
  .restore-point:hover {
    border: solid 1px $mi-anthracite-400;
    background-color: $mi-silver-50;
    display: flex;
    justify-content: space-between;
    padding: 16px;
    margin: 4px 0;

    .restore-point__header-small {
      width: 203px;
    }

    &__name {
      font-family: $mi-typography-font-family;
      font-weight: 700;
      font-size: 14px;
      line-height: 100%;
      margin-bottom: 4px;
      color: $mi-anthracite-800;
    }

    &__options-selected {
      font-family: $mi-typography-font-family;
      font-weight: 400;
      font-size: 12px;
      line-height: 150%;
      vertical-align: bottom;
      color: $mi-anthracite-500;
    }

    .apply-btn {
      display: block;
      height: 32px;
      width: 51px;
      font-size: 14px;
    }

    .drawer-restore-point {
      display: flex;
      padding: 7px;
      margin-left: 2px;
      height: 32px;
    }

    .drawer-restore-point:hover {
      background: $mi-anthracite-50 !important;
    }

    .recently-applied-badge {
      display: none;
    }

    .small-box {
      max-width: 203px;
      width: 203px;
      min-width: 203px;
    }

    .big-box {
      max-width: 291px;
      width: 291px;
      min-width: 291px;
    }
  }

  .truncate-description {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 200px;
    word-break: break-all;
  }

  .truncate-name {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
    max-width: 291px;
  }

  .name-and-badge {
    max-width: 204px;
  }

</style>
